export type MonitoringEventType = 'error' | 'performance';

export interface MonitoringEvent {
  readonly buildId: string;
  readonly type: MonitoringEventType;
  readonly name: string;
  readonly value?: number;
  readonly detail?: string;
  readonly path: string;
  readonly occurredAt: string;
}

const endpoint = import.meta.env.VITE_MONITORING_ENDPOINT?.trim();
const buildId = typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : 'test';
let monitoringStarted = false;

export function initializeMonitoring(): void {
  if (monitoringStarted || typeof window === 'undefined') {
    return;
  }
  monitoringStarted = true;

  window.addEventListener('error', (event) => {
    captureException(event.error ?? event.message, 'window-error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    captureException(event.reason, 'unhandled-rejection');
  });
  window.addEventListener('brick-rogue:service-worker-error', (event) => {
    captureException((event as CustomEvent<unknown>).detail, 'service-worker');
  });

  observeWebVitals();
}

export function captureException(error: unknown, source = 'application'): void {
  const normalized = normalizeError(error);
  reportMonitoringEvent({
    type: 'error',
    name: source,
    detail: `${normalized.name}: ${normalized.message}`,
  });
}

export function captureMessage(message: string, source = 'application'): void {
  reportMonitoringEvent({ type: 'error', name: source, detail: truncate(message, 600) });
}

export function normalizeError(error: unknown): {
  readonly name: string;
  readonly message: string;
} {
  if (error instanceof Error) {
    return { name: truncate(error.name || 'Error', 80), message: truncate(error.message, 500) };
  }
  if (typeof error === 'string') {
    return { name: 'Error', message: truncate(error, 500) };
  }
  return { name: 'UnknownError', message: '未知运行时异常' };
}

function observeWebVitals(): void {
  if (!('PerformanceObserver' in window)) {
    return;
  }

  observe('largest-contentful-paint', (entries) => {
    const latest = entries.at(-1);
    if (latest) reportPerformance('LCP', latest.startTime);
  });
  observe('layout-shift', (entries) => {
    const value = entries.reduce((total, entry) => {
      const shift = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
      return shift.hadRecentInput ? total : total + (shift.value ?? 0);
    }, 0);
    if (value > 0) reportPerformance('CLS', value);
  });
  observe('event', (entries) => {
    const longest = Math.max(0, ...entries.map((entry) => entry.duration));
    if (longest > 0) reportPerformance('INP-candidate', longest);
  });

  window.addEventListener(
    'load',
    () => {
      const navigation = performance.getEntriesByType('navigation')[0] as
        PerformanceNavigationTiming | undefined;
      if (navigation) {
        reportPerformance('TTFB', navigation.responseStart);
      }
    },
    { once: true },
  );
}

function observe(type: string, onEntries: (entries: readonly PerformanceEntry[]) => void): void {
  if (!PerformanceObserver.supportedEntryTypes.includes(type)) {
    return;
  }
  try {
    const observer = new PerformanceObserver((list) => onEntries(list.getEntries()));
    observer.observe({ type, buffered: true });
  } catch {
    // Unsupported performance entry types must never affect gameplay startup.
  }
}

function reportPerformance(name: string, value: number): void {
  reportMonitoringEvent({ type: 'performance', name, value: Math.round(value * 1000) / 1000 });
}

function reportMonitoringEvent(
  event: Pick<MonitoringEvent, 'type' | 'name' | 'value' | 'detail'>,
): void {
  const payload: MonitoringEvent = {
    buildId,
    type: event.type,
    name: truncate(event.name, 80),
    ...(event.value === undefined ? {} : { value: event.value }),
    ...(event.detail === undefined ? {} : { detail: truncate(event.detail, 600) }),
    path: truncate(window.location.pathname, 160),
    occurredAt: new Date().toISOString(),
  };

  window.dispatchEvent(new CustomEvent('brick-rogue:monitoring', { detail: payload }));
  if (!endpoint) {
    return;
  }

  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'application/json' }))) {
      return;
    }
  } catch {
    // Fall through to fetch; monitoring failures must never trigger an error loop.
  }
  void fetch(endpoint, {
    method: 'POST',
    body,
    headers: { 'content-type': 'application/json' },
    keepalive: true,
  }).catch(() => undefined);
}

function truncate(value: string, maximumLength: number): string {
  return value.slice(0, maximumLength);
}
